import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Box,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Tab,
  TabList,
  TabPanel,
  TabPanelProps,
  TabPanels,
  TabProps,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { ReactNode, useMemo, useRef } from "react";
import { FormattedMessage } from "react-intl";
import { Link as RouterLink, useLocation } from "react-router";

import { HOVER_FONT_WEIGHT_TAB, RanaButton } from "@nens/ranalib";
import { useOverflowItems, useRect } from "@nens/react/hooks";
import { objectEntries } from "@nens/shared-utils";

import { BASE_PAGE_OVERFLOW_ID } from "@/components/base-page";
import { RanaPageTransition } from "@/components/rana-page-transition";
import { useRanaSearchState } from "@/hooks/router/rana-search-state";

import { StickyWrapper } from "./sticky-wrapper";

const FOCUS_BORDER_RESET_STYLES = {
  "&:focus:not(:focus-visible)": { outline: "none", boxShadow: "none" },
};

export type RanaTab = {
  title: Required<ReactNode>;
  panel: ReactNode | null; // If panel is null tab will be disabled
  disabledTitle?: string; // If panel is null show disabledTitle on hover if defined, otherwise show nothing
  disabledPanel?: ReactNode; // If panel is null show disabledPanel content if defined, otherwise show nothing
};

interface RanaTabsProps<T extends string> {
  tabs: Record<T, RanaTab>;
  onChange?: (index: number, key: T) => void;
  tabKey?: string;
  defaultTab?: number;
  tabProps?: TabProps;
  tabPanelProps?: TabPanelProps;
  showBorder?: boolean;
  isPageNav?: boolean;
}

export function RanaTabs<T extends string>({
  tabs,
  tabKey,
  tabProps,
  tabPanelProps,
  onChange,
  defaultTab = 0,
  showBorder = true,
  isPageNav = false,
}: RanaTabsProps<T>) {
  const [tabIndex, setTabIndex] = useRanaSearchState(tabKey ?? "no-tabs", defaultTab, {
    clearOthers: "all",
    disableSettingParams: !tabKey,
  });
  const location = useLocation();

  const parentTabRef = useRef<HTMLDivElement | null>(null);
  const parentTabRect = useRect(parentTabRef, true);
  const activeTabRef = useRef<(HTMLButtonElement & HTMLDivElement) | null>(null);
  const tabListRef = useRef<HTMLDivElement | null>(null);
  const activeRect = useRect(activeTabRef, true);

  // Memoize entries so useOverflowItems only resets when the tabs definition changes.
  const tabEntries = useMemo(() => objectEntries(tabs), [tabs]);

  const { visibleItems: visibleEntries, overflowItems: overflowEntries } = useOverflowItems(
    tabListRef,
    tabEntries,
    { direction: "end", paddingRightPx: 80 }
  );

  const activeItem = tabEntries[tabIndex];
  const activeItemIsOverflow = overflowEntries.some(([key]) => key === activeItem?.[0]);

  const notifyTabChange = (index: number) => {
    const tabKeysList = Object.keys(tabs) as T[];
    onChange?.(index, tabKeysList[index]);
    if (isPageNav)
      document.getElementById(BASE_PAGE_OVERFLOW_ID)?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const selectLocalTab = (index: number) => {
    setTabIndex(index);
    notifyTabChange(index);
  };

  // URL-backed tabs clear the existing search params, then add their index.
  // Tabs without tabKey stay local and continue to update useRanaSearchState directly.
  const getTabHref = (index: number) => {
    if (!tabKey) return undefined;

    const searchParams = new URLSearchParams();
    if (index !== defaultTab) searchParams.set(tabKey, String(index));
    const search = searchParams.toString();
    return `${location.pathname}${search ? `?${search}` : ""}${location.hash}`;
  };

  // Only an unmodified primary click activates the current tab. Other clicks
  // must be left to the browser so middle-click and new-tab behavior survives.
  const isModifiedOrNonPrimaryClick = (event: React.MouseEvent) =>
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey;

  const notifyTabClickHandler = (event: React.MouseEvent) => {
    // TabProps keeps the button event type even when the tab renders as a link.
    // if we want to remove the unknown cast, we should create our own onTabclick function
    // and omit the onClick from TabProps
    const onClick = tabProps?.onClick as unknown as ((event: React.MouseEvent) => void) | undefined;
    onClick?.(event);
  };

  // Router-link tabs let the browser navigate. We only run local side effects
  // for a normal primary click; modified clicks must remain native.
  const handleNativeLinkClick = (event: React.MouseEvent, index: number) => {
    notifyTabClickHandler(event);
    if (isModifiedOrNonPrimaryClick(event)) return;

    notifyTabChange(index);
  };

  // Local tabs have no URL destination, so React owns both the selected state
  // and the tab-change side effects.
  const handleLocalTabClick = (event: React.MouseEvent, index: number) => {
    notifyTabClickHandler(event);
    selectLocalTab(index);
  };

  const parentLeft = parentTabRect.left;
  const activeLeft = activeRect.left - parentLeft;

  return (
    <>
      <StickyWrapper isSticky={isPageNav}>
        <Tabs position="relative" index={tabIndex}>
          <Box position="relative" overflow="hidden" ref={parentTabRef}>
            <TabList
              ref={tabListRef}
              overflow="hidden"
              pr={overflowEntries.length > 0 ? 8 : undefined}
              sx={
                !showBorder
                  ? {
                      border: "none",
                      borderBottomColor: "transparent",
                      borderLeftColor: "transparent",
                      borderRightColor: "transparent",
                      borderTopColor: "transparent",
                    }
                  : undefined
              }
            >
              {visibleEntries.map(([key, { title, panel, disabledTitle }], idx) => {
                const isDisabled = !panel;
                const tabHref = isDisabled ? undefined : getTabHref(idx);

                return (
                  <Tab
                    {...(tabHref ? { as: RouterLink, to: tabHref } : {})}
                    ref={idx === tabIndex ? activeTabRef : null}
                    key={key + "-tab"}
                    isDisabled={isDisabled}
                    title={isDisabled ? disabledTitle : undefined}
                    position="relative"
                    {...tabProps}
                    sx={FOCUS_BORDER_RESET_STYLES}
                    _focusVisible={{ boxShadow: "none" }}
                    onClick={(event) =>
                      tabHref ? handleNativeLinkClick(event, idx) : handleLocalTabClick(event, idx)
                    }
                  >
                    <Text position="absolute">{title}</Text>
                    <Text opacity={0} fontWeight={HOVER_FONT_WEIGHT_TAB}>
                      {title}
                    </Text>
                  </Tab>
                );
              })}
            </TabList>
            {/* Animated underline that tracks the active tab */}
            <Box
              {...(activeRect ? { width: activeRect.width + "px", left: activeLeft + "px" } : {})}
              transition="all 400ms"
              transitionTimingFunction="cubic-bezier(0.34, 1.56, 0.64, 1)"
              bottom={0}
              position="absolute"
              h={1}
              bg="brand.analytic"
              zIndex={1}
            />
          </Box>
        </Tabs>
        {/* When tabs overflow, attach the active-tab ref to the menu button so
             the underline indicator still knows where the active item is. */}
        {overflowEntries.length > 0 && (
          <Box
            ref={activeItemIsOverflow ? activeTabRef : null}
            position="absolute"
            right={0}
            top={0}
            bottom={0}
            display="flex"
            alignItems="center"
            mr={2}
          >
            <Menu>
              <MenuButton
                as={RanaButton}
                variant="fourthiary"
                aria-label="More tabs"
                rightIcon={<ChevronDownIcon ml={-1} />}
                pl={2}
              >
                <FormattedMessage id="more" defaultMessage="More" />
              </MenuButton>
              <MenuList>
                <MenuOptionGroup value={activeItem?.[0]}>
                  {overflowEntries.map(([key, { title, panel, disabledTitle }], i) => {
                    const globalIdx = visibleEntries.length + i;
                    const isDisabled = !panel;
                    const tabHref = isDisabled ? undefined : getTabHref(globalIdx);
                    return (
                      <MenuItemOption
                        {...(tabHref ? { as: RouterLink, to: tabHref } : {})}
                        value={key}
                        key={key + "-overflow"}
                        isDisabled={isDisabled}
                        title={isDisabled ? disabledTitle : undefined}
                        onClick={(event) =>
                          tabHref
                            ? handleNativeLinkClick(event, globalIdx)
                            : handleLocalTabClick(event, globalIdx)
                        }
                      >
                        {title}
                      </MenuItemOption>
                    );
                  })}
                </MenuOptionGroup>
              </MenuList>
            </Menu>
          </Box>
        )}
      </StickyWrapper>
      <RanaPageTransition updateKey={tabIndex}>
        <Tabs index={tabIndex}>
          <TabPanels>
            {tabEntries.map(([key, { panel, disabledTitle, disabledPanel }], idx) => (
              <TabPanel {...tabPanelProps} key={key + "-panel"}>
                {idx === tabIndex && (panel ?? disabledPanel ?? disabledTitle)}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </RanaPageTransition>
    </>
  );
}
